/**
 * TransactionInsightsPanel Component
 * Phase 5: Smart Transaction Engine 2.0 - Awareness Cockpit UI
 * 
 * Beautiful dashboard showing:
 * - Spending velocity & trends
 * - Subscription summary
 * - Smart tasks/alerts
 * - Gamification status (streaks, XP, badges)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, TrendingUp, TrendingDown, Flame, Award, Star,
  AlertTriangle, CheckCircle, CreditCard, Zap, ChevronRight,
  ChevronDown, Loader2, BarChart3, RefreshCw, Trophy, Target
} from 'lucide-react';
import { 
  getTransactionInsights, 
  getTransactionTasks, 
  getTransactionGamification 
} from '@/services/aiService';

/**
 * Velocity indicator with animated bar
 */
const VelocityIndicator = ({ velocity }) => {
  if (!velocity) return null;

  const getColor = () => {
    switch (velocity.status) {
      case 'spike': return 'from-red-500 to-orange-500';
      case 'increase': return 'from-amber-500 to-yellow-500';
      case 'stable': return 'from-green-500 to-emerald-500';
      case 'decrease': return 'from-blue-500 to-cyan-500';
      case 'plunge': return 'from-emerald-500 to-teal-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg bg-gradient-to-br ${getColor()} text-white`}>
            {velocity.status === 'spike' || velocity.status === 'increase' ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Spending Velocity
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              vs last month
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {velocity.emoji}
          </div>
        </div>
      </div>
      
      <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
        {velocity.message}
      </div>
      
      {/* Velocity bar */}
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(Math.abs(velocity.percentChange) + 50, 100)}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full bg-gradient-to-r ${getColor()} rounded-full`}
        />
      </div>
      
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>£{velocity.previousTotal?.toFixed(0) || 0}</span>
        <span className={velocity.percentChange > 0 ? 'text-red-500' : 'text-green-500'}>
          {velocity.percentChange > 0 ? '+' : ''}{velocity.percentChange}%
        </span>
        <span>£{velocity.currentTotal?.toFixed(0) || 0}</span>
      </div>
    </div>
  );
};

/**
 * Streak display with fire animation
 */
const StreakDisplay = ({ streak, xp }) => {
  if (!streak) return null;

  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-3xl"
          >
            {streak.streakEmoji || '🔥'}
          </motion.div>
          <div>
            <div className="text-2xl font-bold text-orange-700 dark:text-orange-300">
              {streak.currentStreak} day{streak.currentStreak !== 1 ? 's' : ''}
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-400">
              {streak.streakMessage}
            </div>
          </div>
        </div>
        
        {/* XP badge */}
        {xp && (
          <div className="text-right">
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full text-xs font-medium">
              <Zap className="h-3 w-3" />
              {xp.totalXP} XP
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Level {xp.level}: {xp.title}
            </div>
          </div>
        )}
      </div>
      
      {/* Level progress bar */}
      {xp && xp.progressToNext < 100 && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Level {xp.level}</span>
            <span>{xp.xpToNext} XP to Level {xp.nextLevel}</span>
          </div>
          <div className="h-1.5 bg-orange-200 dark:bg-orange-900/40 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${xp.progressToNext}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Smart tasks list
 */
const SmartTasksList = ({ tasks, summary }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!tasks || tasks.length === 0) return null;

  const displayTasks = isExpanded ? tasks : tasks.slice(0, 3);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      case 'medium': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-white dark:from-violet-900/20 dark:to-gray-800 border border-violet-200 dark:border-violet-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Smart Alerts
            </div>
            <div className="text-xs text-gray-500">
              {summary?.total || tasks.length} items need attention
            </div>
          </div>
        </div>
        {summary?.potentialSavings > 0 && (
          <div className="text-right">
            <div className="text-sm font-medium text-green-600 dark:text-green-400">
              Save ~£{summary.potentialSavings.toFixed(0)}/mo
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {displayTasks.map((task, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 hover:shadow-sm transition-shadow"
          >
            <div className="flex items-start gap-3">
              <span className="text-xl">{task.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {task.title}
                  </span>
                  <span className={`px-1.5 py-0.5 text-xs rounded ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                  {task.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {tasks.length > 3 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 w-full py-2 text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 flex items-center justify-center gap-1"
        >
          {isExpanded ? 'Show less' : `Show ${tasks.length - 3} more`}
          <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
};

/**
 * Subscription summary card
 */
const SubscriptionSummary = ({ subscriptions }) => {
  if (!subscriptions || subscriptions.count === 0) return null;

  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 border border-blue-200 dark:border-blue-800">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
          <CreditCard className="h-4 w-4" />
        </div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Subscriptions
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg">
          <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
            {subscriptions.count}
          </div>
          <div className="text-xs text-gray-500">Active</div>
        </div>
        <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg">
          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
            £{subscriptions.yearlyTotal?.toFixed(0) || 0}
          </div>
          <div className="text-xs text-gray-500">Per Year</div>
        </div>
      </div>

      {subscriptions.list?.slice(0, 3).map((sub, idx) => (
        <div key={idx} className="flex items-center justify-between py-1.5 border-t border-gray-100 dark:border-gray-700 first:border-0">
          <span className="text-sm text-gray-700 dark:text-gray-300">{sub.name}</span>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            £{sub.monthlyEstimate?.toFixed(2)}/mo
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * Badges display
 */
const BadgesDisplay = ({ badges }) => {
  if (!badges || badges.length === 0) return null;

  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Badges Earned
        </span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {badges.map((badge, idx) => (
          <motion.div
            key={badge.id}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: idx * 0.1, type: 'spring' }}
            className="group relative"
          >
            <div className={`
              px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5
              ${badge.tier === 'gold' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' :
                badge.tier === 'silver' ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' :
                'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'}
            `}>
              <span>{badge.emoji}</span>
              <span>{badge.name}</span>
            </div>
            
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {badge.description}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/**
 * Main TransactionInsightsPanel component
 */
const TransactionInsightsPanel = ({ onRefresh }) => {
  const [insights, setInsights] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [insightsRes, tasksRes, gamRes] = await Promise.all([
        getTransactionInsights({ period: 'month' }),
        getTransactionTasks(),
        getTransactionGamification()
      ]);

      setInsights(insightsRes);
      setTasks(tasksRes);
      setGamification(gamRes);
    } catch (err) {
      console.error('Failed to load insights:', err);
      setError('Could not load insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="p-6 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-white dark:from-violet-900/20 dark:via-gray-800 dark:to-gray-800 dark:border-violet-800">
        <div className="flex items-center justify-center gap-3 py-8">
          <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
          <span className="text-gray-600 dark:text-gray-400">Loading insights...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
        <div className="text-center text-gray-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-white dark:from-violet-900/20 dark:via-gray-800 dark:to-gray-800 dark:border-violet-800 overflow-hidden">
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-violet-50/50 dark:hover:bg-violet-900/20 transition-colors cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Smart Insights
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              AI-powered transaction intelligence
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              loadData();
              onRefresh?.();
            }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0 space-y-4">
              {/* Top row: Velocity + Streak */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <VelocityIndicator velocity={insights?.summary?.velocity} />
                <StreakDisplay 
                  streak={gamification?.streak} 
                  xp={gamification?.xp} 
                />
              </div>

              {/* Middle row: Tasks + Subscriptions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SmartTasksList 
                  tasks={tasks?.tasks} 
                  summary={tasks?.summary}
                />
                <SubscriptionSummary subscriptions={insights?.subscriptions} />
              </div>

              {/* Bottom: Badges */}
              {gamification?.badges?.length > 0 && (
                <BadgesDisplay badges={gamification.badges} />
              )}

              {/* Quick insights messages */}
              {insights?.summary?.insights?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {insights.summary.insights.map((insight, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`
                        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm
                        ${insight.type === 'warning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                          insight.type === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}
                      `}
                    >
                      <span>{insight.emoji}</span>
                      <span>{insight.text}</span>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TransactionInsightsPanel;
